import type { Nullable } from "@bounce/util"

export type GmbAddress = {
  addressLines: string
  locality: string | null
  administrativeArea: string | null
  regionCode: string
  postalCode: string | null
}

export type StoreWithAddress = {
  city: { name: string; state: string | null; country: string }
  address?: Nullable<{ street: string; zipCode: string }>
}

export type StoreWithGmb = {
  gmbAddress: GmbAddress | null
  gmbLocationPlaceId: string | null
  gmbExternalId?: string | null
} & StoreWithAddress

let regionFormatter: Intl.DisplayNames | undefined = undefined
/**
 * Format the store address as google maps
 * @example
 * getGmbAddress({ gmbAddress: { addressLines: ["1 Starcross St"], locality: "London", administrativeArea: "Greater London", regionCode: "GB", postalCode: "NW1 2HR" } })
 * // 1 Starcross St, London NW1 2HR, United Kingdom
 */
export const getGmbAddress = (gmbAddress: GmbAddress) => {
  const { addressLines, locality, administrativeArea, regionCode, postalCode } = gmbAddress

  // Convert the region code to the region name
  regionFormatter ??= new Intl.DisplayNames(["en-US"], { type: "region" })
  const region = regionFormatter.of(regionCode) || regionCode

  const zipCode = [administrativeArea || locality, postalCode].filter(Boolean).join(" ").trim()
  const maybeCity = administrativeArea ? locality : administrativeArea

  return [addressLines, maybeCity, zipCode, region].filter(Boolean).join(", ").trim()
}

/**
 * Get the full store address
 * @example
 * getAddress({ city: { name: "London", state: "Greater London", country: "United Kingdom" }, address: { street: "1 Starcross St", zipCode: "NW1 2HR" } })
 * // 1 Starcross St, London NW1 2HR, Greater London, United Kingdom
 */
export const getAddress = (store: StoreWithAddress) => {
  const { address, city } = store
  return [address?.street, city.name, address?.zipCode, city.state, city.country].filter(Boolean).join(", ")
}

/**
 * Get the store google maps embed query "q=place_id:AFD464SDF" or "q=street, city, zipCode
 * @see https://developers.google.com/maps/documentation/embed/get-started
 */
export const getGoogleMapsQuery = (store: StoreWithGmb) => {
  const { gmbLocationPlaceId, gmbExternalId } = store
  const fullAddress = store.gmbAddress ? getGmbAddress(store.gmbAddress) : getAddress(store)

  const placeId = gmbLocationPlaceId || gmbExternalId
  return placeId ? `place_id:${placeId}` : fullAddress
}

/**
 * Get the store google maps url
 * @example getGoogleMapsUrl(store) // https://www.google.com/maps/place?q=place_id:AFD464SDF
 */
export const getGoogleMapsUrl = (store: StoreWithGmb) => {
  return `https://www.google.com/maps/place?q=${getGoogleMapsQuery(store)}`
}
